// src/components/answer/AnswerTable.jsx

import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import AnswerText from "./AnswerText";

/**
 * AnswerTable
 *
 * Converts markdown pipe tables into clean responsive HTML tables.
 *
 * Goals:
 * - Give every table its own independent horizontal scroll container.
 * - Show horizontal scrolling only when that specific table is wider than its viewport.
 * - Reset each table to the far-left position when its content changes.
 * - Keep mobile swipe behavior and desktop scrollbar behavior.
 * - Prevent long paragraphs from stretching the table into one huge line.
 * - Prevent headers from breaking letter-by-letter.
 * - Preserve readable wrapping inside cells.
 * - Add smart column classes so CSS can style numeric, date, short, and long text columns differently.
 * - Keep a light fallback block if content is not a valid table.
 */


function cleanCell(cell) {
  return String(cell ?? "")
    .trim()
    .replace(/^:+/, "")
    .replace(/:+$/, "")
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\\|/g, "|")
    .replace(/\s+/g, " ")
    .trim();
}

function splitPipeAware(value) {
  const input = String(value ?? "");
  const cells = [];
  let current = "";
  let escaped = false;
  let inCode = false;
  let codeTicks = 0;

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];

    if (escaped) {
      current += ch;
      escaped = false;
      continue;
    }

    if (ch === "\\") {
      current += ch;
      escaped = true;
      continue;
    }

    if (ch === "`") {
      let ticks = 1;

      while (
        i + ticks < input.length &&
        input[i + ticks] === "`"
      ) {
        ticks += 1;
      }

      const tickText = "`".repeat(ticks);
      current += tickText;

      if (!inCode) {
        inCode = true;
        codeTicks = ticks;
      } else if (ticks === codeTicks) {
        inCode = false;
        codeTicks = 0;
      }

      i += ticks - 1;
      continue;
    }

    if (ch === "|" && !inCode) {
      cells.push(current);
      current = "";
      continue;
    }

    current += ch;
  }

  cells.push(current);
  return cells;
}

function splitRow(line) {
  let row = String(line ?? "").trim();

  const hadLeadingPipe = row.startsWith("|");
  const hadTrailingPipe =
    row.endsWith("|") &&
    !row.endsWith("\\|");

  if (hadLeadingPipe) {
    row = row.slice(1);
  }

  if (hadTrailingPipe) {
    row = row.slice(0, -1);
  }

  return splitPipeAware(row).map(cleanCell);
}

function isSeparatorRow(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(
    String(line ?? "")
  );
}

function looksLikePipeRow(line) {
  const value = String(line ?? "").trim();

  if (
    !value ||
    !value.includes("|") ||
    isSeparatorRow(value)
  ) {
    return false;
  }

  return splitRow(value).length >= 2;
}

function isRecoverableRowWidth(row, headerCount) {
  if (
    !Array.isArray(row) ||
    headerCount < 2
  ) {
    return false;
  }

  if (row.length === headerCount) {
    return true;
  }

  // One missing trailing blank cell is narrowly recoverable.
  return row.length === headerCount - 1;
}

function normalizeRecoverableRows(headers, rows) {
  const headerCount = headers.length;

  return rows.map((row) => {
    if (row.length === headerCount) {
      return [...row];
    }

    if (row.length === headerCount - 1) {
      return [...row, ""];
    }

    return [...row];
  });
}

function hasMeaningfulHeader(headers) {
  if (
    !Array.isArray(headers) ||
    headers.length < 2
  ) {
    return false;
  }

  const nonEmpty = headers.filter((cell) =>
    String(cell ?? "").trim()
  );

  if (nonEmpty.length < 2) {
    return false;
  }

  // At least one alphabetic header label prevents purely numeric pipe rows from
  // being treated as a separator-less table, while allowing headers like A | B.
  return nonEmpty.some((cell) =>
    /[A-Za-z]/.test(
      String(cell)
    )
  );
}

function hasMeaningfulDataRows(rows) {
  return (
    Array.isArray(rows) &&
    rows.some((row) =>
      row.some((cell) =>
        String(cell ?? "").trim()
      )
    )
  );
}

function allRowsHaveConsistentWidth(rows, expectedCount) {
  if (
    !Array.isArray(rows) ||
    !rows.length ||
    expectedCount < 2
  ) {
    return false;
  }

  return rows.every((row) =>
    isRecoverableRowWidth(
      row,
      expectedCount
    )
  );
}

function inferSimpleTableWithoutSeparator(lines) {
  /*
    Separator-less tables are accepted only with strong structure:
      - every non-empty line is a pipe row
      - at least 3 rows total (header + 2 data rows)
      - at least 2 columns
      - consistent column count
      - header has real label-like text
  */
  const cleanLines = (
    Array.isArray(lines) ? lines : []
  )
    .map((line) =>
      String(line ?? "").trim()
    )
    .filter(Boolean);

  if (cleanLines.length < 3) {
    return null;
  }

  if (!cleanLines.every(looksLikePipeRow)) {
    return null;
  }

  const splitRows =
    cleanLines.map(splitRow);

  const headers =
    splitRows[0] || [];

  if (
    headers.length < 2 ||
    !hasMeaningfulHeader(headers)
  ) {
    return null;
  }

  const usableRows =
    splitRows
      .slice(1)
      .filter((row) =>
        row.some((cell) =>
          String(cell ?? "").trim()
        )
      );

  if (usableRows.length < 2) {
    return null;
  }

  if (
    !allRowsHaveConsistentWidth(
      usableRows,
      headers.length
    )
  ) {
    return null;
  }

  return {
    headers,
    rows:
      normalizeRecoverableRows(
        headers,
        usableRows
      ),
    confidence: "strong-simple",
  };
}

function parseMarkdownTable(value) {
  const lines = String(value ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  const separatorIndex =
    lines.findIndex(isSeparatorRow);

  if (separatorIndex <= 0) {
    const inferred =
      inferSimpleTableWithoutSeparator(
        lines
      );

    if (!inferred) {
      return {
        headers: [],
        rows: [],
        valid: false,
        confidence: "none",
      };
    }

    return {
      ...inferred,
      valid: true,
    };
  }

  const headerIndex =
    separatorIndex - 1;

  const headerLine =
    lines[headerIndex] || "";

  if (!looksLikePipeRow(headerLine)) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  const headers =
    splitRow(headerLine);

  const separatorColumns =
    splitRow(
      lines[separatorIndex]
    ).length;

  if (
    headers.length < 2 ||
    separatorColumns !== headers.length ||
    !hasMeaningfulHeader(headers)
  ) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  const rowLines =
    lines
      .slice(separatorIndex + 1)
      .filter(
        (line) =>
          looksLikePipeRow(line) &&
          !isSeparatorRow(line)
      );

  if (!rowLines.length) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  const rawRows =
    rowLines
      .map(splitRow)
      .filter((row) =>
        row.some((cell) =>
          String(cell ?? "").trim()
        )
      );

  if (
    !rawRows.length ||
    !allRowsHaveConsistentWidth(
      rawRows,
      headers.length
    )
  ) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  const rows =
    normalizeRecoverableRows(
      headers,
      rawRows
    );

  if (!hasMeaningfulDataRows(rows)) {
    return {
      headers: [],
      rows: [],
      valid: false,
      confidence: "none",
    };
  }

  return {
    headers,
    rows,
    valid: true,
    confidence: "markdown",
  };
}

function isNumericLike(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  return /^[$€£₹]?\s*-?\d{1,3}(?:,\d{3})*(?:\.\d+)?%?$|^-?\d+(?:\.\d+)?%?$/.test(
    text,
  );
}

function isDateLike(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  return (
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text) ||
    /^\d{4}-\d{1,2}-\d{1,2}$/.test(text) ||
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\b/i.test(
      text,
    )
  );
}

function isBooleanLike(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return [
    "yes",
    "no",
    "true",
    "false",
    "present",
    "absent",
    "unknown",
    "n/a",
  ].includes(text);
}

function columnStats(headers, rows) {
  return headers.map((header, colIndex) => {
    const values = rows
      .map((row) => String(row[colIndex] || "").trim())
      .filter(Boolean);

    const joined = values.join(" ");
    const avgLen = values.length
      ? values.reduce((sum, value) => sum + value.length, 0) /
        values.length
      : 0;

    const maxLen = values.reduce(
      (max, value) => Math.max(max, value.length),
      0,
    );

    const numericCount = values.filter(isNumericLike).length;
    const dateCount = values.filter(isDateLike).length;
    const booleanCount = values.filter(isBooleanLike).length;

    const numericRatio = values.length
      ? numericCount / values.length
      : 0;

    const dateRatio = values.length
      ? dateCount / values.length
      : 0;

    const booleanRatio = values.length
      ? booleanCount / values.length
      : 0;

    const headerLower = String(header || "").toLowerCase();

    let type = "medium";

    if (numericRatio >= 0.75) {
      type = "numeric";
    } else if (
      dateRatio >= 0.6 ||
      /(date|deadline|effective|expires|period)/i.test(headerLower)
    ) {
      type = "date";
    } else if (
      booleanRatio >= 0.75 ||
      /(status|present|missing|found|yes\/no|risk)/i.test(headerLower)
    ) {
      type = "status";
    } else if (
      maxLen >= 120 ||
      avgLen >= 70 ||
      /(summary|description|explanation|finding|analysis|reason|notes?|details?|comment|issue|risk|recommendation)/i.test(
        headerLower,
      )
    ) {
      type = "long";
    } else if (maxLen <= 18 && avgLen <= 14) {
      type = "short";
    }

    return {
      index: colIndex,
      header,
      type,
      avgLen,
      maxLen,
      joined,
    };
  });
}

function getTableClass(headers, rows) {
  const count = headers.length;
  const hasLongText = rows.some((row) =>
    row.some((cell) => String(cell || "").length >= 120),
  );

  const classes = ["answer-table"];

  if (count <= 3) classes.push("answer-table-compact-cols");
  if (count >= 6) classes.push("answer-table-many-cols");
  if (hasLongText) classes.push("answer-table-has-long-text");

  return classes.join(" ");
}

function getCellClass(colMeta, cellValue, isHeader = false) {
  const classes = [
    "answer-table-cell",
    `answer-table-cell-${colMeta?.type || "medium"}`,
  ];

  if (isHeader) classes.push("answer-table-header-cell");
  if (isNumericLike(cellValue)) {
    classes.push("answer-table-cell-align-number");
  }
  if (isBooleanLike(cellValue)) {
    classes.push("answer-table-cell-status-value");
  }

  return classes.join(" ");
}

function useTableOverflow(scrollRef, contentRef, dependencyKey) {
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    const contentElement = contentRef.current;

    if (!scrollElement || !contentElement) return undefined;

    const measure = () => {
      const overflow =
        contentElement.scrollWidth >
        scrollElement.clientWidth + 1;

      setHasOverflow(overflow);

      /*
       * Every table starts at the far-left position.
       * This prevents a prior scroll position from carrying over when
       * React reuses the same component for new table content.
       */
      if (scrollElement.scrollLeft !== 0) {
        scrollElement.scrollLeft = 0;
      }
    };

    measure();

    let resizeObserver = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(scrollElement);
      resizeObserver.observe(contentElement);
    }

    if (typeof window !== "undefined") {
      window.addEventListener(
        "resize",
        measure
      );
    }

    return () => {
      resizeObserver?.disconnect();

      if (typeof window !== "undefined") {
        window.removeEventListener(
          "resize",
          measure
        );
      }
    };
  }, [scrollRef, contentRef, dependencyKey]);

  return hasOverflow;
}

export default function AnswerTable({ value }) {
  const scrollInstructionId = useId();

  const table = useMemo(
    () => parseMarkdownTable(value),
    [value],
  );

  const columns = useMemo(
    () => columnStats(table.headers, table.rows),
    [table.headers, table.rows],
  );

  const tableClassName = useMemo(
    () => getTableClass(table.headers, table.rows),
    [table.headers, table.rows],
  );

  const dependencyKey = useMemo(
    () =>
      JSON.stringify({
        headers: table.headers,
        rows: table.rows,
      }),
    [table.headers, table.rows],
  );

  const scrollRef = useRef(null);
  const contentRef = useRef(null);

  const hasOverflow = useTableOverflow(
    scrollRef,
    contentRef,
    dependencyKey,
  );

  /*
    Defense in depth:
    AnswerRenderer is the primary table classifier. If malformed pipe content
    or ordinary prose reaches AnswerTable anyway, preserve it as normal answer
    text instead of manufacturing a table or exposing a code-style fallback.
  */
  if (
    !table.valid ||
    !table.headers.length ||
    !table.rows.length
  ) {
    return (
      <AnswerText
        value={String(value ?? "")}
      />
    );
  }

  return (
    <div
      ref={scrollRef}
      className={[
        "answer-table-scroll",
        hasOverflow
          ? "answer-table-scroll-has-overflow"
          : "answer-table-scroll-fits",
      ].join(" ")}
      role="region"
      aria-label={
        hasOverflow
          ? "Scrollable answer table. Swipe or scroll horizontally to view all columns."
          : "Answer table"
      }
      aria-describedby={
        hasOverflow
          ? scrollInstructionId
          : undefined
      }
      tabIndex={hasOverflow ? 0 : -1}
      data-has-horizontal-overflow={
        hasOverflow ? "true" : "false"
      }
    >
      <div
        ref={contentRef}
        className="answer-table-wrap"
      >
        <table className={tableClassName}>
          <thead>
            <tr>
              {table.headers.map((header, index) => (
                <th
                  key={`th-${index}`}
                  scope="col"
                  className={getCellClass(
                    columns[index],
                    header,
                    true,
                  )}
                  data-column-type={
                    columns[index]?.type || "medium"
                  }
                >
                  <span className="answer-table-cell-content">
                    {header || "\u00A0"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={`tr-${rowIndex}`}>
                {table.headers.map((_, cellIndex) => {
                  const cellValue =
                    row[cellIndex] || "\u00A0";

                  return (
                    <td
                      key={`td-${rowIndex}-${cellIndex}`}
                      className={getCellClass(
                        columns[cellIndex],
                        cellValue,
                        false,
                      )}
                      data-column-type={
                        columns[cellIndex]?.type || "medium"
                      }
                    >
                      <span className="answer-table-cell-content">
                        {cellValue}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasOverflow ? (
        <span
          id={scrollInstructionId}
          className="answer-table-scroll-instruction"
          aria-hidden="true"
        >
          Scroll horizontally to view all columns
        </span>
      ) : null}
    </div>
  );
}
